import { AxiosError } from 'axios';

const MAX_MESSAGE_LENGTH = 500;
const MAX_DEPTH = 10;

function isHtmlLike(s: string): boolean {
  const head = s.trimStart();
  if (!head) return false;
  return head[0] === '<';
}

function sanitizeString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed.length > MAX_MESSAGE_LENGTH) return undefined;
  if (isHtmlLike(trimmed)) return undefined;
  return trimmed;
}

function findFirstDrfErrorMessage(
  value: unknown,
  seen: WeakSet<object>,
  depth: number,
): string | undefined {
  if (depth > MAX_DEPTH) return undefined;
  if (typeof value === 'string') return sanitizeString(value);
  if (Array.isArray(value)) {
    if (seen.has(value)) return undefined;
    seen.add(value);
    for (const item of value) {
      const msg = findFirstDrfErrorMessage(item, seen, depth + 1);
      if (msg) return msg;
    }
    return undefined;
  }
  if (value && typeof value === 'object') {
    if (seen.has(value)) return undefined;
    seen.add(value);
    for (const v of Object.values(value as Record<string, unknown>)) {
      const msg = findFirstDrfErrorMessage(v, seen, depth + 1);
      if (msg) return msg;
    }
  }
  return undefined;
}

function messageFromBody(data: unknown, fallback: string): string {
  if (data != null && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const topLevel = sanitizeString(obj.error) ?? sanitizeString(obj.detail);
    if (topLevel) return topLevel;
    const drfMsg = findFirstDrfErrorMessage(obj, new WeakSet<object>(), 0);
    if (drfMsg) return drfMsg;
  }
  return fallback;
}

export function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) return messageFromBody(error.response?.data, fallback);
  if (error instanceof Error) {
    const msg = sanitizeString(error.message);
    if (msg) return msg;
  }
  return fallback;
}

/**
 * The server's `detail` string, but ONLY when `getErrorMessage` cannot carry it:
 * longer than `MAX_MESSAGE_LENGTH`, where the ordinary reader gives up and returns
 * the caller's generic fallback instead. A verbose BackOffice response body
 * (LR-581) is the case that needs it.
 *
 * Returns `''` whenever the ordinary line would do — so a caller that renders this
 * in a monospace block does not turn a human sentence (a 409 status conflict, a
 * DRF 403) into what looks like a stack dump. That distinction is the whole reason
 * the cap lives here and not in the component.
 */
export function getOversizedResponseDetail(error: unknown): string {
  if (!(error instanceof AxiosError)) return '';
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail !== 'string') return '';
  const trimmed = detail.trim();
  return trimmed.length > MAX_MESSAGE_LENGTH ? trimmed : '';
}

/**
 * `getErrorMessage` for a request made with `responseType: 'blob'`.
 *
 * A file download has to declare that response type up front, so axios wraps
 * even a JSON error body in a `Blob` — an object whose own values are empty.
 * `getErrorMessage` therefore walks nothing and returns the fallback, and the
 * server's explanation never reaches the user: the oversized-PDF refusal
 * (LR-571 `_PDF_MAX_LINES`, which names the row count and points at CSV)
 * surfaces as a bare "Request failed with status code 400".
 *
 * Reads the blob and parses it as the JSON body it would have been, then hands
 * off to the same walker, so a downloaded error is sanitized exactly like an
 * ordinary one. Async because `Blob.text()` is. A body that is not JSON — an
 * nginx HTML 502, an empty blob from a torn-down connection — throws in
 * `JSON.parse` and takes the fallback, which is what the HTML guard in
 * `sanitizeString` would have decided anyway.
 */
export async function getFileErrorMessage(
  error: unknown,
  fallback: string,
): Promise<string> {
  if (error instanceof AxiosError && error.response?.data instanceof Blob) {
    try {
      return messageFromBody(JSON.parse(await error.response.data.text()), fallback);
    } catch {
      return fallback;
    }
  }
  return getErrorMessage(error, fallback);
}
